import React, {cloneElement, useContext, useEffect, useState} from 'react';
import {oceanImportIsfList, TableColumn, TableSettings} from "../../../tableSettings";
import AbstractListView, {
  DataFetchResult,ExportRowRenderer,ListCustomFilterRenderer,ListOrderBy,ListRowRenderer
} from "../../common/AbstractListView";
import constants from '../../../constants';
import {Search} from "../../../filtering";
import {
  DateRenderer,DateTimeRenderer,FloatRenderer,NullableCheckboxIconRenderer,
  TextAlignLeftRenderer,TextRenderer
} from '../../../uiControls/SmartTableRenderers';
import JobListViewContext, {IJobListViewContext, RightClickContext} from '../../common/JobListViewContext';
import {ActionButtonItem, borderColors, splitMenuItemsIntoSections} from '../../../ui/ActionButton';
import ToolboxMenuItem from '../../../uiControls/ToolboxMenuItem';
import useFunctionKeys from '../../../hooks/useFunctionKeys';
import IfmsModal from '../../../ui/IfmsModal';
import {JobNames} from '../../../framework.core/JobNames';
import * as XCol from '../../../framework.core/XExportColumn';
import Menu, {IfmsMenuItem} from '../../../ui/menu/Menu';
import ListViewShortcutMenu from '../../common/ListViewShortcutMenu';
import { FmsSubMenu } from '../../../constants/fmsSubMenu';
import { IsfListItem } from '../../../fms.data/models/IsfListItem';
import InfoAreaDrawer from '../../../uiControls/infoArea/InfoAreaDrawer';
import { SuntekSettings, SystemSettings } from '../../../ifmsTypes';
import ApplicationContext from '../../../ApplicationContext';
import { useHistory } from 'react-router';
import { openNewPage, NavigationType } from '../../../util/navigationHelpers';
import useReportViewer from '../../../hooks/useReportViewer';
import { ISFHistoryArgs } from '../../../fms.report/args';
import { DocumentType } from '../../../framework.core/DocumentType';
import IconButton from '../../../ui/IconButton';
import DetailIcon2 from '../../../img/ISF Detail2.svg';
import DetailIcon2_D from '../../../img/ISF Detail_Dark2.svg';
import UsageIcon2 from '../../../img/ISF Usage2.svg';
import UsageIcon2_D from '../../../img/ISF Usage_Dark2.svg';
import FilingIcon2 from '../../../img/ISF2.svg';
import FilingIcon2_D from '../../../img/ISF_Dark2.svg';
import DownIcon2 from '../../../img/ISF Excel Form Download2.svg';
import DownIcon2_D from '../../../img/ISF Excel Form Download_Dark.svg';
import DownIsfIcon2 from '../../../img/AMS - Download to OI2.svg';
import DownIsfIcon2_D from '../../../img/AMS - Download to OI_Dark2.svg';
import UploadIcon2 from '../../../img/EDI-Upload2.svg';
import UploadIcon2_D from '../../../img/EDI-Upload_Dark2.svg';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import { SiteLogType } from '../../../fms.enum';
import SparkleIcon from '../../../img/ai-marker.svg';

function useIsfOperations() {
  const history = useHistory();
  const jobListContext = useContext(JobListViewContext);

  function switchToHblFor(isf:IsfListItem|null) {
    if (!isf) return;

    jobListContext.setFilterForScreenExternally('/common/joblistview/hbl/oceanImport', 'ISFLinkID', 'Isf Link ID', isf.isfLinkIDs, 'u2Date', false);
    openNewPage(history, `/common/joblistview/hbl/oceanImport`, NavigationType.newPage);
  }

  return { switchToHblFor };
}

function useSelectedIsfOperations() {
  const jobListContext = useContext(JobListViewContext);
  const { switchToHblFor } = useIsfOperations();
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const appContext = useContext(ApplicationContext);
  
  async function switchToHbl() {
    const selectedElement = jobListContext.selectedElement as IsfListItem;
    let findHBL = false;

    if (selectedElement?.block && selectedElement?.isfLinkIDs) {
      const checkApiResult = await appContext.api.checkIsfDownload(selectedElement?.isfLinkIDs);


      if (checkApiResult && checkApiResult.success) {
        findHBL = checkApiResult.result;
        if (findHBL) switchToHblFor(selectedElement);
      }
    }
    if (!findHBL) {
      await showGlobalOkDialog(<p>Not Downloaded!</p>);
    }
  }

  return { switchToHbl };
}

function RightClickMenu({dismiss}:{dismiss: VoidFunction}) {
  const { switchToHbl } = useSelectedIsfOperations();
  const jobListContext = useContext(JobListViewContext);

  const items = [
    { label: 'Search', shortcut: 'F5', separator: false, newCol: false },
    { label: 'Go to BL List', shortcut: 'F6', separator: false },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) =>({
    label: <ToolboxMenuItem width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    newCol: i.newCol,
    index,
  }));

  function onMenuItemSelected(item:IfmsMenuItem) {
    switch (item.index) {
      case 0:
        jobListContext.triggerTableSearch('F5');
        break;
      case 1:
        switchToHbl();
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu='rightClickContext'
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: 'nowrap',
      }}
      menuStyle={(i: number) => ({
        maxHeight: '300px',
        overflowY: 'hidden',
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}

function CustomizeTextRendererImpl({ cell, row, data }: any) {
  const l = data[row.index];
  const lastAction = l.lastAction as string;
  const status = cell.value;
  var color = '';

  if (status === 'OK') {
    switch (lastAction) {
    case 'DEL-Accepted': 
      color = '#800080';
      break;
    case 'ADD-Accepted':
    case 'REP-Accepted':
      color = '#008000';
      break;
    case 'ADD-Rejected':
    case 'REP-Rejected':
    case 'DEL-Rejected':
      color = '#e73838';
      break;
    }
  }

  if (color.length > 0)
    return <div id={`${cell.column.id}-${row.index}`} className="ifms-table-align-left" style={{color: color}}>{cell.value}</div>;
  else
    return <div id={`${cell.column.id}-${row.index}`} className="ifms-table-align-left">{cell.value}</div>;
}

function ChbRenderer({ cell, row, data }:any) {
  const l = data[row.index];
  const tooltip = l.cbpCode === 'x' ? undefined : `${l.cbpCode} - ${l.cbpMsg}`;
  return <div id={`${cell.column.id}-${row.index}`} className="ifms-table-default" data-tooltip={tooltip}>{cell.value}</div>;
}

function SparkleRenderer({cell, row, data}: any){
  const l = data[row.index];  
  return <div  className="ifms-table-default" id={`aiAgent_renderer`}>{l.isAiAgent ?<img id={'sparkleIcon'} style={{ position: 'relative', top: '0px' }} src={SparkleIcon} />: <></>}</div>;
}

const gotoLinkIcon = 
<svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12" fill="none">
<path d="M3 9L9 3M5 3H9V7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>

const IMANIFEST_URL = process.env.REACT_APP_IMANIFEST_URL;
function ShipmentIdRenderer({cell, row, data}: any){
  const l = data[row.index];
  const appContext = useContext(ApplicationContext);
  async function gotoIsfDetail(){
        try {
      const apiResult = await appContext.api.getIsfLink('', l.id, l.isfType);

      if (apiResult && apiResult.success) {
        //apiResult.result.item2 : auto Login 여부 (1 : true, 0: false)
        if(!appContext.user?.isSuntekAdmin) await appContext.api.saveSiteAccessLog(SiteLogType.IsfWeb, apiResult.result.item2 ? 1 : 0);
        window.open(`${IMANIFEST_URL}?args=${apiResult.result.item1}`); // Test
      }
    } catch (error) {
      console.error('ISF Detail Error:', error);
    }
  }

  return <div id={`${cell.column.id}-${row.index}`}
    style={{ display: "flex", alignItems: "center", justifyContent: "space-between",width: "100%",paddingRight: "4px",}}>
    {cell.value}
    {(l.createdBy.toLowerCase() === "@aiagent" && cell.value) ? <a style={{cursor: 'pointer'}} onClick={async ()=>{await gotoIsfDetail()}}>{gotoLinkIcon}</a>: <></>}
  </div>;
}

function useSelectedOperations() {
  const jobListContext = useContext(JobListViewContext);
  const history = useHistory();

  const selectedElement = jobListContext.selectedElement as IsfListItem;

  async function moveToUsageReport() {
    if (!selectedElement) {
      return;
    }
    const viewUrl = `/isf/isfUsage`;

    openNewPage(history, viewUrl, NavigationType.newPage);
  }
  return {moveToUsageReport};
}

function IsfListCommands() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const [selectedPopupMenuItem, setSelectedPopupMenuItem] = useState<ActionButtonItem|null>(null);
  const { viewReport } = useReportViewer();
  const history = useHistory();

  const {moveToUsageReport} = useSelectedOperations();

  const selectedElement = jobListContext.selectedElement as IsfListItem;

  const onPopupClose = () => {
    setSelectedPopupMenuItem(null);
  }
  
  async function onSubMenuClick(fmsSubMenu: FmsSubMenu) {
    setSelectedPopupMenuItem(null);

    if (!selectedElement) {
      return;
    }
  }

    async function print() {
      if (!selectedElement) {
        return;
      }
  
      const title = `ISF History [${selectedElement?.hblNo}]`;
      const args = new ISFHistoryArgs({
        docType: DocumentType.ISFHistory,
        formCaption: title,
        subject: title,
        jobId: selectedElement?.id ?? 0,
        isfNo: selectedElement?.isfNo
      });
      viewReport(args, "", true);
  }

  async function handleIsfLink(url: string = '') {
    try {
      const apiResult = await appContext.api.getIsfLink(url);

      if (apiResult && apiResult.success) {
        //apiResult.result.item2 : auto Login 여부 (1 : true, 0: false)
        if(!appContext.user?.isSuntekAdmin) await appContext.api.saveSiteAccessLog(SiteLogType.IsfWeb, apiResult.result.item2 ? 1 : 0);
        window.open(`${IMANIFEST_URL}?args=${apiResult.result.item1}`);
      }
    } catch (error) {
      console.error('ISF Filing Error:', error);
    }
  }

  async function handleIsfDetailLink(id: number, isfType: string) {
    try {
      const apiResult = await appContext.api.getIsfLink('', id, isfType);

      if (apiResult && apiResult.success) {
        //apiResult.result.item2 : auto Login 여부 (1 : true, 0: false)
        if(!appContext.user?.isSuntekAdmin) await appContext.api.saveSiteAccessLog(SiteLogType.IsfWeb, apiResult.result.item2 ? 1 : 0);
         window.open(`${IMANIFEST_URL}?args=${apiResult.result.item1}`);
      }
    } catch (error) {
      console.error('ISF Detail Error:', error);
    }
  }

  const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/template/Suntek ISF Filing Template.xlsx'; // Relative path to your public file
    link.download = 'Suntek ISF Filing Template.xlsx'; // Suggested filename for download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function isfDownloadView() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.id;

    const viewUrl = `/oceanImport/download/isf/${id}`;

    openNewPage(history, viewUrl, NavigationType.newPage);
  }

  return (
    <div className="ifms-common-no-vertical-separator">
      {/*appContext.suntekSettings?.company?.billingsTabVersion === 2 ? <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"} /> : <></>*/}
      {/* <IconButton data-tooltip='ISF Detail' name='ifs-list-isfDetail-button' onClick={() => handleIsfDetailLink(selectedElement?.id, selectedElement?.isfType)} icon={appContext.useDarkMode ? FilingIcon2_D : FilingIcon2} /> */}
      {/* <IconButton data-tooltip='ISF Filing' name='ifs-list-amsFiling-button' onClick={() => handleIsfLink()} icon={appContext.useDarkMode ? FilingIcon2_D : FilingIcon2} />
      <IconButton data-tooltip='ISF Upload' name='isf-list-isfUpload-button' onClick={() => handleIsfLink('upload')} icon={appContext.useDarkMode ? UploadIcon2_D : UploadIcon2} />
      <IconButton data-tooltip='Std. ISF Excel Form (Download)' name='isf-list-xldownload-button' onClick={downloadTemplate} icon={appContext.useDarkMode ? DownIcon2_D : DownIcon2} />
      <IconButton
        name='ifs-list-isfdownload-button'
        disabled={!selectedElement}
        onClick={() => isfDownloadView()}
        data-tooltip='Download' icon={appContext.useDarkMode ? DownIsfIcon2_D : DownIsfIcon2}
      ></IconButton>
      <IconButton data-tooltip='ISF Detail' name='ifs-list-filingStatus-button' onClick={print} icon={appContext.useDarkMode ? DetailIcon2_D : DetailIcon2} />
      <IconButton data-tooltip='ISF Usage' name='ifs-list-UsageReport-button' onClick={moveToUsageReport} icon={appContext.useDarkMode ? UsageIcon2_D : UsageIcon2} /> */}
      {!!selectedPopupMenuItem && !!selectedPopupMenuItem.popupComponent ? (
        <IfmsModal isBlueHeader cancelOnEsc onCancel={onPopupClose} draggable isOpen={!!selectedPopupMenuItem} title={selectedPopupMenuItem?.popupLabel || selectedPopupMenuItem?.label} width={`${selectedPopupMenuItem?.popupWidth || 760}px`}>
          {cloneElement(selectedPopupMenuItem.popupComponent!, { onPopupClose })}
        </IfmsModal>
      ) : null}
      {!!selectedPopupMenuItem && !!selectedPopupMenuItem.subMenu ? onSubMenuClick(selectedPopupMenuItem.subMenu) : null}
    </div>
  );
}

function IsfListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

// function formatData(data:Array<IsfListItem>) {
//   return data.map(d => ({
//     ...d,
//   }));
// }

function IsfListViewExtras() {
  useFunctionKeys({onFunction: onFunctionKey});

  const jobListContext = useContext(JobListViewContext);
  const { switchToHbl } = useSelectedIsfOperations();

  const [unprocessedFkey, setUnprocessedFkey] = useState<string|null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }

      doProcessFkey();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === 'F6') {
      switchToHbl();
    } 
    else if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    }
    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey:string) {
    if (['F6', 'F11'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }

  return (
    <InfoAreaDrawer
      //hideLogHistory={true}
      showContent={jobListContext.infoAreaContent}
      jobName={JobNames.ISF}
      jobId={(jobListContext.selectedElement as IsfListItem|null)?.id ?? 0}
      onClose={() => jobListContext.setInfoAreaContent('none')}
    />
  );
}

/**
 * Concrete implementation for ISF List screen
 *
 * @class IsfListView
 * @extends {AbstractListView}
 */
 class IsfListView extends AbstractListView {
  async fetchAndFormatData(api: any, search: Search, pageNum: number, limit: number): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(JobNames.ISF, 'IsfListItem',
    IsfListItem.calculateSearchParameters(search, IsfListItem.MapSearchFields, []),
    pageNum,
    limit,
    search.pageSize,
    search.pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0 ) {
      return {data: [], totalCount: 0};
    }

    return { data: IsfListItem.formatData(apiResult.entities), totalCount: apiResult.totalCount};
  }

  async exportFormatDataWithExcel(api: any, search: Search, pageNum: number, limit: number, pageSize: number, pagePerBatch: number): Promise<Array<any>> {
    const apiResult = await api.getListItems(JobNames.ISF, 'IsfListItem',
      IsfListItem.calculateSearchParameters(search, IsfListItem.MapSearchFields, []),
      pageNum,
      limit,
      pageSize,
      pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return [];
    }

    return IsfListItem.formatData(apiResult.entities);
  }

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
    ];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.oceanImportMbl;
  }

  getRowRenderers(opType:string, systemSettings:SystemSettings, suntekSettings:SuntekSettings): ListRowRenderer[] {
    const useBranch = systemSettings?.enableBranch ?? false;
    return IsfListItem.getExportCollection(useBranch, this.user?.isSuntekAdmin ?? false).map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if(x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;
      if(x.dataIndex === "isAiAgent")  colRenderer.Cell = SparkleRenderer;
      else if(x.dataIndex === "isfStatus") colRenderer.Cell = CustomizeTextRendererImpl;
      else if(x.dataIndex === "chbCode") colRenderer.Cell = ChbRenderer;
      else if(x.dataIndex === 'shipmentId') colRenderer.Cell = ShipmentIdRenderer;
      else if(x.type === XCol.XCellType.Bool || x.type === XCol.XCellType.StrBool) colRenderer.Cell = NullableCheckboxIconRenderer;
      else if(x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if(x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeRenderer;
      else if(x.type === XCol.XCellType.Double) colRenderer.Cell = FloatRenderer;
      return colRenderer;
    });
  }
  
  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({accessor: 'isfStatus', changedValue: (value: any)=> { return value['isfStatus']}});
    return colRenderer;
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'id',
      orderByAscending: false,
    };
  }

  getTableSettings(opType:string, systemSettings:SystemSettings, suntekSettings:SuntekSettings): TableSettings {
    const tsettings = oceanImportIsfList;
    const useBranch = systemSettings?.enableBranch ?? false;
    const cols = IsfListItem.getExportCollection(useBranch, this.user?.isSuntekAdmin ?? false);

    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });
    return tsettings;
    
  }

  renderCommands() {
    return (
      <IsfListCommands />
    );
  }

  renderBeforeConfigureButton() {
    return (
      <IsfListToolbar />
    );
  }


  renderExtras() {
    return <IsfListViewExtras />
  }

  getViewUrlFor(row: IsfListItem) {
    return '';
  }

  getListViewTitle() {
    return 'ISF List';
  };

  renderRowExtras(rowId: number | null, context: IJobListViewContext): React.ReactNode|null {
    return null;
  }

  renderAdditionalCommands() {
    return null;
  }

  hasRightClickContext(): boolean {
    return true;
  }

  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }
};

export default IsfListView;
